import { Card, CardContent, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import React, { useEffect, useMemo, useState } from "react";
import Spinner from "../../../common/components/Spinner";
import { useErrorHandler } from "../../../common/hooks";
import { PieChartModel } from "../../Models/Dashboards/PieChartModel";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";
import { ChartDefaultColors } from "./ChartDefaultColors";

const PieChartComponent: React.FC<IComponentProps<PieChartModel>> = ({ component, context }) => {
  const errorHandler = useErrorHandler();

  const [data, setData] = useState<any>(null);
  const [dataPending, setDataPending] = useState<boolean>(false);

  const handleLoadData = async () => {
    if (!component.GetData) {
      console.warn("GetData expression is not defined.");
      return;
    }

    setDataPending(true);
    try {
      const result = await context.Evaluator.evaluate(component.GetData, context);
      setData(result);
    } catch (error) {
      errorHandler(error);
    } finally {
      setDataPending(false);
    }
  };

  useEffect(() => {
    handleLoadData();
  }, [component.GetData]);

  const chartData = useMemo(() => {
    if (!data) return [];

    return component.Properties.map((prop, index) => ({
      id: prop.Field || prop.Label,
      label: prop.Label,
      value: prop.Field ? parseFloat(data[prop.Field]) || 0 : 0,
      color: prop.Color || ChartDefaultColors[index % ChartDefaultColors.length],
    }));
  }, [data, component.Properties]);

  const componentLayoutSx = getComponentLayout(component);

  return (
    <Card sx={componentLayoutSx}>
      <CardContent sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {component.HeaderLabel && (
          <Typography variant="h6" gutterBottom>
            {component.HeaderLabel}
          </Typography>
        )}
        {dataPending ? (
          <Spinner />
        ) : (
          <PieChart
            series={[
              {
                data: chartData,
                highlightScope: { fade: "global", highlight: "item" },
                faded: { innerRadius: 30, additionalRadius: -30, color: "gray" },
              },
            ]}
            margin={0}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default PieChartComponent;
